import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Wifi, Shield, Video, Phone, Activity, Bell, FileText, MessageSquare, 
  Download, TrendingUp, Clock, AlertCircle, CheckCircle, XCircle, LogOut,
  User, Mail, CreditCard, Plus, Send, QrCode, Copy, X, ExternalLink, Upload, Package,
  Globe, Zap, Gauge
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import MakePaymentModal from '../components/payment/MakePaymentModal';
import PaymentProcessModal from '../components/payment/PaymentProcessModal';
import PaymentConfirmationModal from '../components/payment/PaymentConfirmationModal';
import ThankYouModal from '../components/payment/ThankYouModal';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [invoicesData, setInvoicesData] = useState(null);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    category: '',
    subject: '',
    description: '',
    priority: 'Medium'
  });
  
  // Payment Flow States
  const [paymentStep, setPaymentStep] = useState(0); // 0: closed, 1: initial form, 2: process, 3: confirmation, 4: thank you
  const [paymentData, setPaymentData] = useState(null);
  
  // New Feature States
  const [showAlreadyPaidModal, setShowAlreadyPaidModal] = useState(false);
  const [showRequestServiceModal, setShowRequestServiceModal] = useState(false);
  
  const [alreadyPaidForm, setAlreadyPaidForm] = useState({
    invoiceId: '',
    amount: '',
    paymentMethod: '',
    transactionId: '',
    paymentDate: new Date().toISOString().split('T')[0],
    receiptFile: null,
    remarks: ''
  });
  
  const [serviceRequestForm, setServiceRequestForm] = useState({
    serviceType: '',
    planSpeed: '',
    installationAddress: '',
    contactNumber: '',
    remarks: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/customer-login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.userType !== 'customer') {
      toast.error('Access denied. Customer account required.');
      navigate('/customer-login');
      return;
    }

    setUser(parsedUser);
    fetchAllData(token);
  }, [navigate]);

  const fetchAllData = async (token) => {
    try {
      await Promise.all([
        fetchDashboardData(token),
        fetchInvoices(token),
        fetchServiceRequests(token)
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    }
  };

  const fetchInvoices = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoicesData(data);
      }
    } catch (error) {
      console.error('Invoices fetch error:', error);
    }
  };

  const fetchServiceRequests = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer/service-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServiceRequests(data.serviceRequests || []);
      }
    } catch (error) {
      console.error('Service requests fetch error:', error);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_BASE_URL}/api/customer/support-tickets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category: ticketForm.category,
          subject: ticketForm.subject,
          description: ticketForm.description,
          attachments: []
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Support ticket created successfully! Ticket ID: ${data.ticketId}`);
        setShowTicketModal(false);
        setTicketForm({ category: '', subject: '', description: '', priority: 'Medium' });
        fetchServiceRequests(token);
      } else {
        toast.error(data.detail || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Create ticket error:', error);
      toast.error('Network error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleAlreadyPaidSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      const formData = new FormData();
      Object.keys(alreadyPaidForm).forEach(key => {
        if (alreadyPaidForm[key]) {
          formData.append(key, alreadyPaidForm[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/api/customer/submit-payment-proof`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Payment details submitted successfully! Our team will verify and update.');
        setShowAlreadyPaidModal(false);
        setAlreadyPaidForm({
          invoiceId: '',
          amount: '',
          paymentMethod: '',
          transactionId: '',
          paymentDate: new Date().toISOString().split('T')[0],
          receiptFile: null,
          remarks: ''
        });
        // Refresh invoices
        const invoicesResponse = await fetch(`${API_BASE_URL}/api/customer/invoices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (invoicesResponse.ok) {
          const data = await invoicesResponse.json();
          setInvoicesData(data);
        }
      } else {
        toast.error('Failed to submit payment details');
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      toast.error('Error submitting payment details');
    }
  };

  const handleRequestService = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer/service-requests`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(serviceRequestForm)
      });
      
      if (response.ok) {
        toast.success('Service request submitted successfully! Our team will contact you soon.');
        setShowRequestServiceModal(false);
        setServiceRequestForm({
          serviceType: '',
          planSpeed: '',
          installationAddress: '',
          contactNumber: '',
          remarks: ''
        });
        fetchServiceRequests(token);
      } else {
        toast.error('Failed to submit service request');
      }
    } catch (error) {
      console.error('Error requesting service:', error);
      toast.error('Error submitting service request');
    }
  };


  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'internet': return <Wifi className="h-8 w-8" />;
      case 'leaseline':
      case 'lease_line': return <Activity className="h-8 w-8" />;
      case 'cctv': return <Video className="h-8 w-8" />;
      case 'networking': return <Shield className="h-8 w-8" />;
      case 'voip': return <Phone className="h-8 w-8" />;
      default: return <Activity className="h-8 w-8" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800 border-green-300';
      case 'Downtime': return 'bg-red-100 text-red-800 border-red-300';
      case 'Suspended': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active': return <CheckCircle className="h-5 w-5" />;
      case 'Downtime': return <AlertCircle className="h-5 w-5" />;
      case 'Suspended': return <XCircle className="h-5 w-5" />;
      default: return <Activity className="h-5 w-5" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-purple-100 text-purple-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Mock usage data for charts
  const usageData = [
    { date: '01/11', download: 45, upload: 12, uptime: 99 },
    { date: '02/11', download: 52, upload: 15, uptime: 100 },
    { date: '03/11', download: 48, upload: 13, uptime: 98 },
    { date: '04/11', download: 61, upload: 18, uptime: 100 },
    { date: '05/11', download: 55, upload: 16, uptime: 99 },
    { date: '06/11', download: 67, upload: 20, uptime: 100 },
    { date: '07/11', download: 58, upload: 17, uptime: 99 }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img 
                src="/netix-logo-main.png" 
                alt="Netix Fibernet" 
                className="h-12 bg-white rounded p-1"
              />
              <div>
                <h1 className="text-2xl font-bold">Customer Portal</h1>
                <p className="text-purple-100">Welcome back, {user?.firstName}!</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="bg-white text-purple-600 border-white hover:bg-purple-100"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b-2 border-purple-200 shadow-md">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'home', label: 'Home', icon: Activity },
              { id: 'services', label: 'Active Services', icon: Wifi },
              { id: 'billing', label: 'Billing', icon: CreditCard },
              { id: 'support', label: 'Support', icon: MessageSquare },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 font-semibold transition-all border-b-4 flex items-center gap-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-purple-600 text-purple-600 bg-purple-50'
                      : 'border-transparent text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'home' && (
          <div className="space-y-6">
            {/* Advertising Banner - Enhanced */}
            <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
              <img 
                src="https://images.unsplash.com/photo-1506818144585-74b29c980d4b"
                alt="High Speed Internet"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-purple-900/80 to-pink-900/70 flex items-center">
                <div className="px-12 relative z-10">
                  <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold text-white mb-4 animate-pulse">
                    ⚡ Netix Fibernet
                  </div>
                  <h2 className="text-5xl font-black text-white mb-3 animate-gradient bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                    Experience Lightning-Fast Internet
                  </h2>
                  <p className="text-xl text-blue-100 font-semibold mb-4">Powered by Advanced Fiber Optic Technology</p>
                  <div className="flex gap-4 items-center text-white/90 text-sm">
                    <div className="flex items-center gap-2">
                      <Wifi className="h-5 w-5" />
                      <span>1 Gbps Speed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      <span>99.9% Uptime</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      <span>24/7 Support</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile & Stats Cards - Enhanced */}
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="md:col-span-1 border-0 shadow-xl overflow-hidden group hover:scale-105 transition-all duration-300">
                <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 p-6 text-white relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-4">
                      <User className="h-5 w-5" />
                      <span className="font-bold">Profile</span>
                    </div>
                    <div className="text-center space-y-3">
                      <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto border-4 border-white/30 shadow-lg">
                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                      </div>
                      <div>
                        <p className="font-bold text-lg">{user?.firstName} {user?.lastName}</p>
                        <p className="text-sm text-blue-100 flex items-center justify-center gap-1 mt-1">
                          <Mail className="h-4 w-4" />
                          {user?.email}
                        </p>
                        <div className="mt-3 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full inline-block">
                          <p className="text-xs font-semibold">
                            ID: {dashboardData?.accountId}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-green-50 to-emerald-50 hover:scale-105 group">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-2">Active Services</p>
                      <p className="text-4xl font-black text-green-600">{dashboardData?.activeServices || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">Running smoothly</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-4 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                      <CheckCircle className="h-8 w-8 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-red-50 to-rose-50 hover:scale-105 group">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-2">Downtime Alerts</p>
                      <p className="text-4xl font-black text-red-600">{dashboardData?.downtimeAlerts || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">Active alerts</p>
                    </div>
                    <div className="bg-gradient-to-br from-red-500 to-rose-600 p-4 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                      <AlertCircle className="h-8 w-8 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-yellow-50 to-amber-50 hover:scale-105 group">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-2">Pending Invoices</p>
                      <p className="text-4xl font-black text-yellow-600">{dashboardData?.pendingInvoices || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">Awaiting payment</p>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-500 to-amber-600 p-4 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                      <FileText className="h-8 w-8 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>


            {/* Quick Actions - Enhanced */}
            <Card className="border-0 shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Quick Actions</h3>
                    <p className="text-blue-100 text-sm">Fast access to important features</p>
                  </div>
                </div>
              </div>
              <CardContent className="p-6 bg-gradient-to-br from-gray-50 to-white">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {/* Visit Website */}
                  <Button
                    onClick={() => window.open('https://www.netixfibernet.com', '_blank')}
                    className="bg-gradient-to-br from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 h-auto py-8 flex-col gap-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                  >
                    <div className="bg-white/20 p-3 rounded-full group-hover:scale-110 transition-transform">
                      <Globe className="h-8 w-8" />
                    </div>
                    <span className="text-sm font-bold">Visit Website</span>
                  </Button>
                  
                  {/* Make Payment / Already Paid */}
                  <Button
                    onClick={() => navigate('/make-payment')}
                    className="bg-gradient-to-br from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 h-auto py-8 flex-col gap-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                  >
                    <div className="bg-white/20 p-3 rounded-full group-hover:scale-110 transition-transform">
                      <CreditCard className="h-8 w-8" />
                    </div>
                    <span className="text-sm font-bold">Make Payment</span>
                  </Button>
                  
                  {/* Update Payment */}
                  <Button
                    onClick={() => window.open('https://www.netixfibernet.com/make-payment', '_blank')}
                    className="bg-gradient-to-br from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 h-auto py-8 flex-col gap-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                  >
                    <div className="bg-white/20 p-3 rounded-full group-hover:scale-110 transition-transform">
                      <Wifi className="h-8 w-8" />
                    </div>
                    <span className="text-sm font-bold">Update Payment</span>
                  </Button>
                  
                  {/* Speed Test */}
                  <Button
                    onClick={() => navigate('/speed-test')}
                    className="bg-gradient-to-br from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 h-auto py-8 flex-col gap-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                  >
                    <div className="bg-white/20 p-3 rounded-full group-hover:scale-110 transition-transform">
                      <Gauge className="h-8 w-8" />
                    </div>
                    <span className="text-sm font-bold">Speed Test</span>
                  </Button>
                  
                  {/* Raise Ticket */}
                  <Button
                    onClick={() => setShowTicketModal(true)}
                    className="bg-gradient-to-br from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-auto py-8 flex-col gap-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                  >
                    <div className="bg-white/20 p-3 rounded-full group-hover:scale-110 transition-transform">
                      <MessageSquare className="h-8 w-8" />
                    </div>
                    <span className="text-sm font-bold">Raise Ticket</span>
                  </Button>
                  
                  {/* Request Service */}
                  <Button
                    onClick={() => setShowRequestServiceModal(true)}
                    className="bg-gradient-to-br from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 h-auto py-8 flex-col gap-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                  >
                    <div className="bg-white/20 p-3 rounded-full group-hover:scale-110 transition-transform">
                      <Plus className="h-8 w-8" />
                    </div>
                    <span className="text-sm font-bold">Request Service</span>
                  </Button>
                  
                  {/* View Invoices */}
                  <Button
                    onClick={() => setActiveTab('invoices')}
                    className="bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 h-auto py-8 flex-col gap-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                  >
                    <div className="bg-white/20 p-3 rounded-full group-hover:scale-110 transition-transform">
                      <FileText className="h-8 w-8" />
                    </div>
                    <span className="text-sm font-bold">View Invoices</span>
                  </Button>
                </div>
              </CardContent>
            </Card>


            {/* Active Services Overview */}
            <Card className="border-2 border-purple-200 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-purple-100 to-blue-100">
                <CardTitle className="text-2xl">Active Services Overview</CardTitle>
                <CardDescription>Manage your active Netix Fibernet services</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {dashboardData?.services && dashboardData.services.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {dashboardData.services.map((service) => (
                      <Card key={service._id} className="border-2 hover:shadow-xl transition-all">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white">
                                {getServiceIcon(service.serviceType)}
                              </div>
                              <div>
                                <h3 className="font-bold text-lg">{service.serviceName}</h3>
                                {service.speed && <p className="text-sm text-gray-600">{service.speed}</p>}
                              </div>
                            </div>
                          </div>
                          
                          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 ${getStatusColor(service.status)} mb-3`}>
                            {getStatusIcon(service.status)}
                            <span className="font-semibold">{service.status}</span>
                          </div>

                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Monthly Charge:</span>
                              <span className="font-semibold">₹{service.monthlyCharge}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Start Date:</span>
                              <span className="font-semibold">{new Date(service.startDate).toLocaleDateString()}</span>
                            </div>
                            {service.renewalDate && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Renewal Date:</span>
                                <span className="font-semibold">{new Date(service.renewalDate).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Activity className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No active services yet</p>
                    <Button
                      onClick={() => setActiveTab('services')}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Request New Service
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-blue-200 hover:shadow-xl transition-all cursor-pointer" onClick={() => setActiveTab('billing')}>
                <CardContent className="pt-6 text-center">
                  <CreditCard className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-bold text-lg mb-2">View Invoices</h3>
                  <p className="text-sm text-gray-600">Check pending and paid invoices</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200 hover:shadow-xl transition-all cursor-pointer" onClick={() => setActiveTab('support')}>
                <CardContent className="pt-6 text-center">
                  <MessageSquare className="h-12 w-12 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-bold text-lg mb-2">Support Tickets</h3>
                  <p className="text-sm text-gray-600">Create and track service requests</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 hover:shadow-xl transition-all cursor-pointer" onClick={() => setActiveTab('analytics')}>
                <CardContent className="pt-6 text-center">
                  <TrendingUp className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <h3 className="font-bold text-lg mb-2">Usage Reports</h3>
                  <p className="text-sm text-gray-600">View bandwidth and analytics</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-6">
            {/* Service Banner */}
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1604869515882-4d10fa4b0492"
                alt="Fiber Optic Technology"
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/70 flex items-center justify-center">
                <h2 className="text-3xl font-bold text-white">Our Fiber Optic Services</h2>
              </div>
            </div>

            <Card className="border-2 border-purple-200 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-purple-100 to-blue-100">
                <CardTitle className="text-2xl">Request New Service</CardTitle>
                <CardDescription>Choose from our range of premium services</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {[
                    { name: 'Fiber Broadband', icon: Wifi, desc: 'High-speed internet', color: 'blue' },
                    { name: 'Lease Line Internet', icon: Activity, desc: 'Dedicated bandwidth', color: 'indigo' },
                    { name: 'Enterprise Networking', icon: Shield, desc: 'Business solutions', color: 'green' },
                    { name: 'CCTV & Security', icon: Video, desc: 'Surveillance systems', color: 'red' },
                    { name: 'VoIP Services', icon: Phone, desc: 'Voice communication', color: 'purple' }
                  ].map((service, idx) => {
                    const Icon = service.icon;
                    return (
                      <Card key={idx} className={`border-2 border-${service.color}-200 hover:shadow-xl transition-all cursor-pointer`}>
                        <CardContent className="pt-6 text-center">
                          <div className={`w-16 h-16 bg-gradient-to-br from-${service.color}-400 to-${service.color}-600 rounded-full flex items-center justify-center text-white mx-auto mb-4`}>
                            <Icon className="h-8 w-8" />
                          </div>
                          <h3 className="font-bold text-lg mb-2">{service.name}</h3>
                          <p className="text-sm text-gray-600 mb-4">{service.desc}</p>
                          <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                            Request Service
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-yellow-200 shadow-lg">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Total Pending</p>
                    <p className="text-4xl font-bold text-yellow-600">₹{invoicesData?.totalPending || 0}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 shadow-lg">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Total Paid</p>
                    <p className="text-4xl font-bold text-green-600">₹{invoicesData?.totalPaid || 0}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Button 
                      onClick={() => setPaymentStep(1)}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
                      size="lg"
                    >
                      <CreditCard className="mr-2 h-5 w-5" />
                      Make Payment
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Pending Invoices */}
            <Card className="border-2 border-yellow-200 shadow-xl">
              <CardHeader className="bg-yellow-50">
                <CardTitle className="text-2xl text-yellow-800">Pending Invoices</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {invoicesData?.pendingInvoices && invoicesData.pendingInvoices.length > 0 ? (
                  <div className="space-y-4">
                    {invoicesData.pendingInvoices.map((invoice) => (
                      <Card key={invoice._id} className="border-2 border-yellow-100">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-bold text-lg">Invoice #{invoice.invoiceNumber}</p>
                              <p className="text-sm text-gray-600">{invoice.serviceName}</p>
                              <p className="text-xs text-gray-500 mt-1">Due: {new Date(invoice.dueDate).toLocaleDateString()}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-yellow-600">₹{invoice.totalAmount}</p>
                              <Button className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white" size="sm">
                                Pay Now
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No pending invoices</p>
                )}
              </CardContent>
            </Card>

            {/* Paid Invoices */}
            <Card className="border-2 border-green-200 shadow-xl">
              <CardHeader className="bg-green-50">
                <CardTitle className="text-2xl text-green-800">Paid Invoices</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {invoicesData?.paidInvoices && invoicesData.paidInvoices.length > 0 ? (
                  <div className="space-y-4">
                    {invoicesData.paidInvoices.map((invoice) => (
                      <Card key={invoice._id} className="border-2 border-green-100">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-bold text-lg">Invoice #{invoice.invoiceNumber}</p>
                              <p className="text-sm text-gray-600">{invoice.serviceName}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Paid: {invoice.paidDate ? new Date(invoice.paidDate).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-green-600">₹{invoice.totalAmount}</p>
                              <Button variant="outline" size="sm" className="mt-2">
                                <Download className="h-4 w-4 mr-2" />
                                Download
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No paid invoices yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'support' && (
          <div className="space-y-6">
            <Card className="border-2 border-purple-200 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-purple-100 to-blue-100">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl">Support Tickets</CardTitle>
                    <CardDescription>Track and manage your service requests</CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowTicketModal(true)}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Ticket
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {serviceRequests.length > 0 ? (
                  <div className="space-y-4">
                    {serviceRequests.map((ticket) => (
                      <Card key={ticket._id} className="border-2 hover:shadow-lg transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <p className="font-bold text-lg">{ticket.subject}</p>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTicketStatusColor(ticket.status)}`}>
                                  {ticket.status}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                                  {ticket.priority}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{ticket.description}</p>
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>Ticket: {ticket.ticketNumber}</span>
                                <span>Category: {ticket.category}</span>
                                <span>Created: {new Date(ticket.createdAt).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No support tickets yet</p>
                    <Button
                      onClick={() => setShowTicketModal(true)}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Ticket
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Analytics Banner */}
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1594915440248-1e419eba6611"
                alt="Network Analytics"
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/70 flex items-center justify-center">
                <h2 className="text-3xl font-bold text-white">Usage Analytics & Reports</h2>
              </div>
            </div>

            {/* Usage Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Download</p>
                      <p className="text-3xl font-bold text-blue-600">378 GB</p>
                    </div>
                    <Download className="h-12 w-12 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200 shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Upload</p>
                      <p className="text-3xl font-bold text-purple-600">111 GB</p>
                    </div>
                    <TrendingUp className="h-12 w-12 text-purple-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Avg. Uptime</p>
                      <p className="text-3xl font-bold text-green-600">99.3%</p>
                    </div>
                    <CheckCircle className="h-12 w-12 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Bandwidth Usage Chart */}
            <Card className="border-2 border-blue-200 shadow-xl">
              <CardHeader className="bg-blue-50">
                <CardTitle className="text-2xl">Bandwidth Usage (Last 7 Days)</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={usageData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis label={{ value: 'GB', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="download" stroke="#2563eb" strokeWidth={2} name="Download (GB)" />
                    <Line type="monotone" dataKey="upload" stroke="#9333ea" strokeWidth={2} name="Upload (GB)" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Uptime Chart */}
            <Card className="border-2 border-green-200 shadow-xl">
              <CardHeader className="bg-green-50">
                <CardTitle className="text-2xl">Service Uptime (Last 7 Days)</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={usageData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[95, 100]} label={{ value: '%', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="uptime" fill="#10b981" name="Uptime %" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Payment Flow Modals */}
      <MakePaymentModal
        isOpen={paymentStep === 1}
        onClose={() => setPaymentStep(0)}
        onNext={(data) => {
          setPaymentData(data);
          setPaymentStep(2);
        }}
      />

      <PaymentProcessModal
        isOpen={paymentStep === 2}
        onClose={() => setPaymentStep(0)}
        onNext={(data) => {
          setPaymentData(data);
          setPaymentStep(3);
        }}
        customerData={paymentData}
      />

      <PaymentConfirmationModal
        isOpen={paymentStep === 3}
        onClose={() => setPaymentStep(0)}
        onSuccess={() => {
          setPaymentStep(4);
        }}
        paymentData={paymentData}
      />

      <ThankYouModal
        isOpen={paymentStep === 4}
        onClose={() => {
          setPaymentStep(0);
          setPaymentData(null);
        }}
      />

      {/* New Ticket Modal */}
      {showTicketModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl border-2 border-purple-200 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
              <div className="flex justify-between items-center">
                <CardTitle className="text-2xl">Create Support Ticket</CardTitle>
                <button onClick={() => setShowTicketModal(false)}>
                  <X className="h-6 w-6" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleCreateTicket} className="space-y-4">
                <div>
                  <Label>Category *</Label>
                  <select
                    value={ticketForm.category}
                    onChange={(e) => setTicketForm({...ticketForm, category: e.target.value})}
                    className="w-full mt-2 p-2 border-2 border-gray-200 rounded-lg"
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="Technical Issue">Technical Issue</option>
                    <option value="Billing Query">Billing Query</option>
                    <option value="Installation Request">Installation Request</option>
                    <option value="Complaint">Complaint</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <Label>Subject *</Label>
                  <Input
                    type="text"
                    placeholder="Brief description of the issue"
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                    className="mt-2"
                    required
                  />
                </div>

                <div>
                  <Label>Description *</Label>
                  <Textarea
                    placeholder="Provide detailed information about your issue..."
                    value={ticketForm.description}
                    onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})}
                    className="mt-2 min-h-32"
                    required
                  />
                </div>

                <div>
                  <Label>Priority</Label>
                  <select
                    value={ticketForm.priority}
                    onChange={(e) => setTicketForm({...ticketForm, priority: e.target.value})}
                    className="w-full mt-2 p-2 border-2 border-gray-200 rounded-lg"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>

                <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                  <Send className="h-4 w-4 mr-2" />
                  Submit Ticket
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Already Paid Modal */}
      {showAlreadyPaidModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8 border-2 border-blue-200 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
              <div className="flex justify-between items-center">
                <CardTitle className="text-2xl">Submit Payment Details</CardTitle>
                <button onClick={() => setShowAlreadyPaidModal(false)}>
                  <X className="h-6 w-6" />
                </button>
              </div>
              <CardDescription className="text-blue-100">
                Already made a payment? Submit proof here for verification
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleAlreadyPaidSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Invoice ID *</Label>
                    <Input
                      required
                      value={alreadyPaidForm.invoiceId}
                      onChange={(e) => setAlreadyPaidForm({...alreadyPaidForm, invoiceId: e.target.value})}
                      placeholder="Invoice number"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Amount Paid *</Label>
                    <Input
                      required
                      type="number"
                      value={alreadyPaidForm.amount}
                      onChange={(e) => setAlreadyPaidForm({...alreadyPaidForm, amount: e.target.value})}
                      placeholder="₹"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Payment Method *</Label>
                    <select
                      required
                      value={alreadyPaidForm.paymentMethod}
                      onChange={(e) => setAlreadyPaidForm({...alreadyPaidForm, paymentMethod: e.target.value})}
                      className="w-full mt-2 p-2 border-2 border-gray-200 rounded-lg"
                    >
                      <option value="">Select method</option>
                      <option value="UPI">UPI</option>
                      <option value="Net Banking">Net Banking</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="Cash">Cash</option>
                    </select>
                  </div>
                  <div>
                    <Label>Transaction/Reference ID *</Label>
                    <Input
                      required
                      value={alreadyPaidForm.transactionId}
                      onChange={(e) => setAlreadyPaidForm({...alreadyPaidForm, transactionId: e.target.value})}
                      placeholder="Transaction ID"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Payment Date *</Label>
                    <Input
                      required
                      type="date"
                      value={alreadyPaidForm.paymentDate}
                      onChange={(e) => setAlreadyPaidForm({...alreadyPaidForm, paymentDate: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Upload Receipt/Screenshot</Label>
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setAlreadyPaidForm({...alreadyPaidForm, receiptFile: e.target.files[0]})}
                      className="mt-2"
                    />
                  </div>
                </div>
                <div>
                  <Label>Remarks/Notes</Label>
                  <Textarea
                    rows={3}
                    value={alreadyPaidForm.remarks}
                    onChange={(e) => setAlreadyPaidForm({...alreadyPaidForm, remarks: e.target.value})}
                    placeholder="Any additional information..."
                    className="mt-2"
                  />
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>📝 Note:</strong> Your payment details will be verified by our team within 24 hours. 
                    You'll receive a confirmation email once verified.
                  </p>
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
                  <Upload className="h-4 w-4 mr-2" />
                  Submit Payment Details
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Request Service Modal */}
      {showRequestServiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl border-2 border-orange-200 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
              <div className="flex justify-between items-center">
                <CardTitle className="text-2xl">Request New Service</CardTitle>
                <button onClick={() => setShowRequestServiceModal(false)}>
                  <X className="h-6 w-6" />
                </button>
              </div>
              <CardDescription className="text-orange-100">
                Tell us what service you need, and we'll get in touch!
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleRequestService} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Service Type *</Label>
                    <select
                      required
                      value={serviceRequestForm.serviceType}
                      onChange={(e) => setServiceRequestForm({...serviceRequestForm, serviceType: e.target.value})}
                      className="w-full mt-2 p-2 border-2 border-gray-200 rounded-lg"
                    >
                      <option value="">Select service type</option>
                      <option value="Broadband">Broadband</option>
                      <option value="FTTH">FTTH (Fiber to Home)</option>
                      <option value="Leased Line">Internet Leased Line</option>
                      <option value="OTT Services">OTT Services</option>
                    </select>
                  </div>
                  <div>
                    <Label>Plan Speed *</Label>
                    <select
                      required
                      value={serviceRequestForm.planSpeed}
                      onChange={(e) => setServiceRequestForm({...serviceRequestForm, planSpeed: e.target.value})}
                      className="w-full mt-2 p-2 border-2 border-gray-200 rounded-lg"
                    >
                      <option value="">Select speed</option>
                      <option value="5 Mbps">5 Mbps</option>
                      <option value="50 Mbps">50 Mbps</option>
                      <option value="100 Mbps">100 Mbps</option>
                      <option value="200 Mbps">200 Mbps</option>
                      <option value="500 Mbps">500 Mbps</option>
                      <option value="1 Gbps">1 Gbps</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Installation Address *</Label>
                  <Textarea
                    required
                    rows={3}
                    value={serviceRequestForm.installationAddress}
                    onChange={(e) => setServiceRequestForm({...serviceRequestForm, installationAddress: e.target.value})}
                    placeholder="Complete installation address with pincode"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Contact Number *</Label>
                  <Input
                    required
                    type="tel"
                    value={serviceRequestForm.contactNumber}
                    onChange={(e) => setServiceRequestForm({...serviceRequestForm, contactNumber: e.target.value})}
                    placeholder="10-digit mobile number"
                    maxLength="10"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Special Requirements/Remarks</Label>
                  <Textarea
                    rows={3}
                    value={serviceRequestForm.remarks}
                    onChange={(e) => setServiceRequestForm({...serviceRequestForm, remarks: e.target.value})}
                    placeholder="Any special requirements or notes..."
                    className="mt-2"
                  />
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-800">
                    <strong>📞 Quick Response:</strong> Our team will contact you within 24 hours to discuss 
                    your service requirements and schedule installation.
                  </p>
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white">
                  <Package className="h-4 w-4 mr-2" />
                  Submit Service Request
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
